import { capitalize, Typography } from '@mui/material';
import { formatDate } from '@strength/utils';
import { ReactNode, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  CartesianGrid,
  ComposedChart,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { DateRange } from '../../../../components/dates/RangeCalendar';
import { getLocale } from '../../../../utils/i18n';
import { WorkoutWithCycleAndSession } from '../../types';
import { ActualWorkout, PhaseSpan } from '../types';
import {
  BowlingDay,
  clampDaysToDateRange,
  computeBowlingDays,
} from './bowling-day';
import { BowlingDayTooltip } from './ChartTooltip';
import {
  LegendSwatch,
  renderPhaseBands,
  renderTodayReferenceLine,
  TodayLegendItem,
} from './ChartScaffold';
import { computeTodayLine, xAxisTickInterval } from './chart-utils';

export const PLANNED_COLOR = '#84ADFF';
export const ACTUAL_COLOR = '#155EEF';
export const ACWR_COLOR = '#D83D32';

const RENDER_ACWR = false;

/**
 * State derived from the chart's inputs, exposed to the `renderBars` callback
 * so it can adjust based on whether actual data exists (e.g. omit the actual
 * series in side-by-side mode to keep planned bars centered).
 */
export type BowlingDayChartState = {
  hasActual: boolean;
};

/**
 * Shared scaffold for the two BowlingDay-based charts (Side-by-Side and
 * Stacked). Owns all derived state, axes, tooltip, phase bands, today line,
 * and conditional ACWR overlay. Callers supply the Recharts `<Bar>` elements
 * via `renderBars`.
 */
export const BowlingDayChart = ({
  title,
  actualWorkouts,
  prescriptionWorkouts,
  phases,
  dateRange,
  renderBars,
}: {
  title: string;
  actualWorkouts: ActualWorkout[];
  prescriptionWorkouts: WorkoutWithCycleAndSession[];
  phases: PhaseSpan[];
  dateRange: DateRange;
  renderBars: (state: BowlingDayChartState) => ReactNode;
}) => {
  const { t } = useTranslation();

  // Aggregate against the full input so ACWR rolling windows always see
  // pre-range history. The dateRange narrows what's rendered, not what's
  // aggregated.
  const fullData = useMemo(
    () => computeBowlingDays(prescriptionWorkouts, actualWorkouts),
    [prescriptionWorkouts, actualWorkouts],
  );
  const data: BowlingDay[] = useMemo(
    () => clampDaysToDateRange(fullData, dateRange),
    [fullData, dateRange],
  );

  const { todayIso, showTodayLine } = computeTodayLine(data);
  const firstDay = data[0]?.day;
  const lastDay = data[data.length - 1]?.day;

  const hasAcwr = RENDER_ACWR && data.some((d) => d.acwr !== null);
  const hasActual = data.some((d) => d.actual !== null);

  return (
    <div className="pr-10">
      <div className="flex flex-row items-center justify-between">
        <Typography variant="h4">{title}</Typography>
        <Legend
          showTodayLine={showTodayLine}
          showAcwr={hasAcwr}
          showActual={hasActual}
        />
      </div>
      <ResponsiveContainer width="100%" height={350} className="mt-4">
        <ComposedChart data={data} margin={{ left: 5, bottom: 30 }}>
          <CartesianGrid stroke="#f5f5f5" vertical={false} />
          <XAxis
            dataKey="day"
            type="category"
            scale="band"
            padding="gap"
            interval={xAxisTickInterval(data.length)}
            tick={{ className: 'text-[10px]' }}
            tickFormatter={(value) =>
              formatDate(value, getLocale(), { month: 'short', day: 'numeric' })
            }
            label={{
              value: capitalize(t('day')),
              position: 'insideBottom',
              offset: -5,
              className: 'text-xs text-semibold',
            }}
          />
          <YAxis
            tick={{ className: 'text-[10px]' }}
            domain={[0, (dataMax: number) => Math.ceil(dataMax * 1.2)]}
            label={{
              value: capitalize(t('bowls')),
              angle: -90,
              position: 'insideLeft',
              className: 'text-xs text-semibold',
            }}
          />
          {hasAcwr && (
            <YAxis
              yAxisId="acwr"
              orientation="right"
              tick={{ className: 'text-[10px]' }}
            />
          )}
          <Tooltip
            wrapperStyle={{
              width: 'auto',
              maxWidth: 'none',
              minWidth: '220px',
            }}
            content={({ active, payload }) => {
              const first = payload?.[0];
              if (!active || !first) return null;
              const row = first.payload as BowlingDay;
              return (
                <BowlingDayTooltip
                  row={row}
                  phases={phases}
                  showAcwr={hasAcwr}
                  plannedColor={PLANNED_COLOR}
                  actualColor={ACTUAL_COLOR}
                  acwrColor={ACWR_COLOR}
                />
              );
            }}
          />
          {renderPhaseBands({ phases, firstDay, lastDay })}
          {renderTodayReferenceLine({ todayIso, showTodayLine })}
          {renderBars({ hasActual })}
          {hasAcwr && (
            <Line
              yAxisId="acwr"
              type="monotone"
              dataKey="acwr"
              name="ACWR"
              stroke={ACWR_COLOR}
              dot={false}
              isAnimationActive={false}
              connectNulls
            />
          )}
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
};

const Legend = ({
  showTodayLine,
  showAcwr,
  showActual,
}: {
  showTodayLine: boolean;
  showAcwr: boolean;
  showActual: boolean;
}) => {
  const { t } = useTranslation();
  return (
    <div className="flex flex-row flex-wrap items-center gap-3">
      <LegendSwatch color={PLANNED_COLOR} label={t('planned')} />
      {showActual && <LegendSwatch color={ACTUAL_COLOR} label={t('actuals')} />}
      {showAcwr && <LegendSwatch color={ACWR_COLOR} label="ACWR" />}
      {showTodayLine ? <TodayLegendItem /> : null}
    </div>
  );
};
